import {
    CallExpressionShape,
    Shape,
    TemplateExpressionShape,
    TemplateSpanShape,
    TemplateValueShape,
} from '@servicenow/sdk-build-core'
import type { Source } from '@servicenow/sdk-build-core'
import { DATAPILL_CALLEE } from '../constants/playbook-plugin-constants'

type LiteralPart = { type: 'literal'; text: string }
type ExprPart = { type: 'expr'; shape: Shape }
type Part = LiteralPart | ExprPart

/**
 * Converts a `TemplateValueShape` into the equivalent `field=value^field=value`
 * string representation, preserving dataPill expressions as template spans.
 *
 * Returns a `TemplateExpressionShape` when the value contains dataPills, or a
 * plain `StringShape` when all values are static. Non-TemplateValueShape inputs
 * are returned unchanged.
 *
 * ## Why this is needed
 *
 * `TemplateValue({ approval: wfa.playbook.dataPill(x), work_notes: 'Updated' })`
 * is a `CallExpressionShape` — the playbook pill resolver only handles
 * `StringShape`, `TemplateExpressionShape`, and bare dataPill calls. Converting
 * to a template expression lets both the pill resolver and the field validator
 * process the value through their standard paths.
 *
 * ## Example
 *
 * ```ts
 * TemplateValue({
 *     short_description: `No laptop for: ${wfa.playbook.dataPill(params.trigger.current.name)}`,
 *     priority: 2,
 * })
 * ```
 *
 * becomes the equivalent of:
 *
 * ```ts
 * `short_description=No laptop for: ${wfa.playbook.dataPill(params.trigger.current.name)}^priority=2`
 * ```
 *
 * @returns The original shape unchanged if it is not a `TemplateValueShape`.
 */
export function normalizeTemplateValue(shape: Shape): Shape {
    if (!(shape instanceof TemplateValueShape)) {
        return shape
    }

    const source = shape.getSource()
    const entries = shape.getTemplateValue().entries()

    if (entries.length === 0) {
        return shape
    }

    const parts: Part[] = []

    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i]!
        if (i > 0) {
            parts.push({ type: 'literal', text: '^' })
        }
        parts.push({ type: 'literal', text: `${entry[0]}=` })
        flattenValueIntoParts(entry[1], parts)
    }

    return assembleTemplateExpression(source, parts)
}

/**
 * Decomposes a property value shape into literal and expression parts.
 *
 * - dataPill calls → expression part (preserved for pill resolution)
 * - Template expressions → head literal + flattened spans (so nested
 *   dataPills are preserved while literal text is inlined)
 * - String/number literals → literal text (with `^` escaped to `^^`)
 * - Anything else → stringified as literal (best-effort)
 */
function flattenValueIntoParts(value: Shape, parts: Part[]): void {
    // Direct dataPill call: wfa.playbook.dataPill(...)
    if (value instanceof CallExpressionShape && value.getCallee() === DATAPILL_CALLEE) {
        parts.push({ type: 'expr', shape: value })
        return
    }

    // Template expression: `prefix ${dataPill} suffix`
    // Walk its head literal and spans so dataPill expressions are preserved
    // as expression parts while surrounding text becomes literal parts.
    if (value instanceof TemplateExpressionShape) {
        const headText = value.getLiteralText()
        if (headText) {
            parts.push({ type: 'literal', text: escapeCarets(headText) })
        }
        for (const span of value.getSpans()) {
            flattenValueIntoParts(span.getExpression(), parts)
            const trailingText = span.getLiteralText()
            if (trailingText) {
                parts.push({ type: 'literal', text: escapeCarets(trailingText) })
            }
        }
        return
    }

    // Plain string or number — inline as escaped literal text.
    const strShape = value.ifString()
    if (strShape) {
        parts.push({ type: 'literal', text: escapeCarets(strShape.getValue()) })
        return
    }
    const numShape = value.ifNumber()
    if (numShape) {
        parts.push({ type: 'literal', text: String(numShape.getValue()) })
        return
    }

    // Fallback: stringify whatever we have.
    parts.push({ type: 'literal', text: escapeCarets(value.toString().getValue()) })
}

/**
 * Assembles a flat parts list into a `TemplateExpressionShape` (when expression
 * parts are present) or a plain `StringShape` (when everything is literal).
 */
function assembleTemplateExpression(source: Source, parts: Part[]): Shape {
    // Merge consecutive literal parts.
    const merged: Part[] = []
    for (const part of parts) {
        const last = merged[merged.length - 1]
        if (part.type === 'literal' && last?.type === 'literal') {
            last.text += part.text
        } else {
            merged.push({ ...part })
        }
    }

    // All literals → return a plain StringShape.
    if (merged.every((p): p is LiteralPart => p.type === 'literal')) {
        const text = merged.map((p) => p.text).join('')
        return Shape.from(source, text).asString()
    }

    // Build head literal (everything before the first expression).
    let headText = ''
    let startIdx = 0
    const first = merged[0]
    if (first?.type === 'literal') {
        headText = first.text
        startIdx = 1
    }

    // Build spans: each expression paired with the literal text that follows it.
    const spans: TemplateSpanShape[] = []
    for (let i = startIdx; i < merged.length; i++) {
        const part = merged[i]!
        if (part.type !== 'expr') {
            continue
        }
        let trailingText = ''
        const next = merged[i + 1]
        if (next?.type === 'literal') {
            trailingText = next.text
            i++
        }
        spans.push(new TemplateSpanShape({ source, expression: part.shape, literalText: trailingText }))
    }

    return new TemplateExpressionShape({ source, literalText: headText, spans })
}

/** Escapes `^` as `^^` to avoid conflicts with the field separator. */
function escapeCarets(text: string): string {
    return text.replace(/\^/g, '^^')
}
